import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { menuSize, placeMenu } from '../utils/menuPlacement'
import LanguageSelector from './LanguageSelector'
import './glassMenu.css'
import './ToolsMenu.css'

function ExportGlyph() {
  return <img src="/Icon download.png" alt="" className="glass-menu-icon" />
}

function Chevron() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
      <path d="M7 4l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

export default function ToolsMenu({
  isOpen,
  onClose,
  buttonRef,
  onExportJSON,
  onExportCSV,
  onExportPDF,
  onExportHTML,
  onImport,
  onPaste,
  onSetBudget,
  onShare,
  onCompare,
  onCustomize,
  hasBudget = false,
  copiedItemsCount = 0,
  showLanguage = false,
}) {
  const menuRef = useRef(null)
  const subRef = useRef(null)
  const reducedMotion = usePrefersReducedMotion()
  const [coords, setCoords] = useState({ left: 0, top: 0 })
  const [subCoords, setSubCoords] = useState({ left: 0, top: 0 })
  const [showExport, setShowExport] = useState(false)
  const [activeId, setActiveId] = useState('export')

  const items = useMemo(() => {
    const rows = [
      { id: 'export', label: 'Exporter', icon: '/Icon download.png', submenu: true },
      { id: 'import', label: 'Importer', icon: '/Icon upload.png' },
      { id: 'sep-1', separator: true },
    ]
    if (onSetBudget) {
      rows.push({
        id: 'budget',
        label: hasBudget ? 'Modifier le budget' : 'Définir le budget',
        icon: '/Icon euro symbol.png',
      })
    }
    if (onCustomize) {
      rows.push({ id: 'appearance', label: 'Personnaliser l’apparence', icon: '/Icon photo size select actual.png' })
    }
    rows.push({ id: 'sep-2', separator: true })
    rows.push({
      id: 'paste',
      label: copiedItemsCount > 0 ? `Coller (${copiedItemsCount})` : 'Coller',
      icon: '/Icon paste.png',
      disabled: copiedItemsCount <= 0,
    })
    if (onShare) rows.push({ id: 'share', label: 'Partager le panier', icon: '/Icon ios share.png' })
    if (onCompare) rows.push({ id: 'compare', label: 'Comparer avec un autre panier', icon: '/Icon compare.png' })
    return rows
  }, [copiedItemsCount, hasBudget, onCompare, onCustomize, onSetBudget, onShare])

  const actionable = items.filter((item) => !item.separator)

  useLayoutEffect(() => {
    if (!isOpen) return
    const anchor = buttonRef?.current?.getBoundingClientRect?.()
    const size = menuSize(menuRef.current)
    const x = anchor ? anchor.right - size.width : 24
    const y = anchor ? anchor.bottom + 8 : 96
    setCoords(placeMenu({
      x,
      y,
      width: size.width,
      height: size.height,
      vw: window.innerWidth,
      vh: window.innerHeight,
    }))
  }, [buttonRef, isOpen, items.length])

  useLayoutEffect(() => {
    if (!isOpen || !showExport) return
    const menu = menuRef.current?.getBoundingClientRect()
    const size = menuSize(subRef.current)
    const x = (menu?.right || coords.left) + 6
    const y = menu?.top || coords.top
    setSubCoords(placeMenu({
      x,
      y,
      width: size.width,
      height: size.height,
      vw: window.innerWidth,
      vh: window.innerHeight,
    }))
  }, [coords.left, coords.top, isOpen, showExport])

  useEffect(() => {
    if (!isOpen) {
      setShowExport(false)
      return undefined
    }
    const onPointer = (event) => {
      const inMenu = menuRef.current?.contains(event.target)
      const inSub = subRef.current?.contains(event.target)
      const inButton = buttonRef?.current?.contains?.(event.target)
      const inLanguage = event.target?.closest?.('.language-menu-root')
      if (!inMenu && !inSub && !inButton && !inLanguage) onClose?.()
    }
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        if (showExport) setShowExport(false)
        else onClose?.()
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [buttonRef, isOpen, onClose, showExport])

  if (!isOpen || typeof document === 'undefined') return null

  const run = (id) => {
    if (id === 'export') {
      setShowExport((open) => !open)
      setActiveId('export')
      return
    }
    setShowExport(false)
    if (id === 'import') onImport?.()
    if (id === 'budget') onSetBudget?.()
    if (id === 'appearance') onCustomize?.()
    if (id === 'paste') onPaste?.()
    if (id === 'share') onShare?.()
    if (id === 'compare') onCompare?.()
    if (id !== 'export') onClose?.()
  }

  const moveActive = (delta) => {
    const index = Math.max(0, actionable.findIndex((item) => item.id === activeId))
    const next = actionable[(index + delta + actionable.length) % actionable.length]
    setActiveId(next.id)
    if (next.id !== 'export') setShowExport(false)
  }

  const onMenuKey = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveActive(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveActive(-1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveId(actionable[0].id)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveId(actionable[actionable.length - 1].id)
    } else if (event.key === 'ArrowRight' && activeId === 'export') {
      event.preventDefault()
      setShowExport(true)
    } else if (event.key === 'ArrowLeft' && showExport) {
      event.preventDefault()
      setShowExport(false)
    }
  }

  return createPortal(
    <>
      <div className="glass-menu-root tools-menu-root" style={{ left: coords.left, top: coords.top }} data-testid="tools-menu">
        <div
          ref={menuRef}
          className={`lg-panel glass-menu tools-menu-panel${reducedMotion ? '' : ' is-animated'}`}
          role="menu"
          aria-label="Outils du panier"
          onKeyDown={onMenuKey}
        >
          <div className="lg-panel-frost" />
          <div className="lg-panel-tint" />
          <div className="lg-panel-shine" />
          <ul className="lg-panel-body glass-menu-list">
            {showLanguage ? (
              <>
                <li role="none">
                  <LanguageSelector variant="tools-item" position="right" />
                </li>
                <li role="separator" className="glass-menu-sep" />
              </>
            ) : null}
            {items.map((item) => (
              item.separator ? (
                <li key={item.id} role="separator" className="glass-menu-sep" />
              ) : (
                <li key={item.id} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className={`glass-menu-item${item.id === activeId ? ' is-active' : ''}`}
                    disabled={item.disabled}
                    aria-haspopup={item.submenu ? 'menu' : undefined}
                    aria-expanded={item.submenu ? showExport : undefined}
                    onMouseEnter={() => {
                      setActiveId(item.id)
                      setShowExport(item.id === 'export')
                    }}
                    onClick={() => run(item.id)}
                  >
                    <img src={item.icon} alt="" className="glass-menu-icon" />
                    <span className="glass-menu-label">{item.label}</span>
                    <span className="glass-menu-trail" aria-hidden="true">
                      {item.submenu ? <Chevron /> : null}
                    </span>
                  </button>
                </li>
              )
            ))}
          </ul>
        </div>
      </div>
      {showExport ? (
        <div className="glass-menu-root" style={{ left: subCoords.left, top: subCoords.top }}>
          <div
            ref={subRef}
            className={`lg-panel glass-menu tools-submenu-panel${reducedMotion ? '' : ' is-animated'}`}
            role="menu"
            aria-label="Formats d’export"
          >
            <div className="lg-panel-frost" />
            <div className="lg-panel-tint" />
            <div className="lg-panel-shine" />
            <ul className="lg-panel-body glass-menu-list">
              {[
                { id: 'json', label: 'Exporter en JSON', fn: onExportJSON },
                { id: 'csv', label: 'Exporter en CSV', fn: onExportCSV },
                { id: 'pdf', label: 'Exporter en PDF', fn: onExportPDF },
                { id: 'html', label: 'Exporter en HTML', fn: onExportHTML },
              ].map((entry) => (
                <li key={entry.id} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="glass-menu-item"
                    onClick={() => {
                      entry.fn?.()
                      onClose?.()
                    }}
                  >
                    <ExportGlyph />
                    <span className="glass-menu-label">{entry.label}</span>
                    <span className="glass-menu-trail" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </>,
    document.body,
  )
}
